import { Fragment, useEffect, useMemo, useState } from 'react';
import DataTable from 'react-data-table-component';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { listarConceptos, filtrarConceptos, limpiarConceptos } from '../../../actions/conceptosExternos';

import NuevoConceptoModal from './modals/NuevoConceptoModal';
import EditarConceptoModal from './modals/EditarConceptoModal';
import EliminarConceptoModal from './modals/EliminarConceptoModal';

const formatMoney = (n) => {
    const v = Number(n || 0);
    return `$${new Intl.NumberFormat('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(v)}`;
};

const renderBadgeTipo = (tipo) => {
    const t = Number(tipo);
    if (t === 1) return <span className="badge bg-primary">Producto</span>;
    if (t === 2) return <span className="badge bg-info text-dark">Servicio</span>;
    return <span className="badge bg-secondary">Sin definir</span>;
};

const ConceptosProyecto = ({ idProyecto }) => {
    const dispatch = useDispatch();

    const { filtrado } = useSelector(state => state.conceptosExternos);

    const [textoFiltro, setTextoFiltro] = useState('');
    const [modalNuevo, setModalNuevo] = useState(false);
    const [conceptoEditar, setConceptoEditar] = useState(null);
    const [conceptoEliminar, setConceptoEliminar] = useState(null);

    useEffect(() => {
        if (!idProyecto) return;

        dispatch(listarConceptos({ id_proyecto: idProyecto }));

        return () => {
            dispatch(limpiarConceptos());
        };
    }, [dispatch, idProyecto]);

    const onChangeFiltroTabla = (e) => {
        const val = e.target.value;
        setTextoFiltro(val);
        dispatch(filtrarConceptos(val));
    };

    const columns = useMemo(() => ([
        {
            name: 'Tipo',
            width: '120px',
            cell: row => renderBadgeTipo(row?.tipo)
        },
        {
            name: 'Orden de salida',
            selector: row => row?.orden_salida?.folio || 'Directo al proyecto',
            sortable: true,
            wrap: true,
            grow: 1,
            cell: row => (
                row?.orden_salida?._id
                    ? <Link to={`/proyectos/ordenes/detalle/${row.orden_salida._id}`}>{row.orden_salida.folio}</Link>
                    : <span className="text-muted">Directo al proyecto</span>
            )
        },
        {
            name: 'Detalle',
            selector: row => row?.detalle || 'Sin definir',
            sortable: true,
            wrap: true,
            grow: 2
        },
        {
            name: 'Cantidad',
            selector: row => row?.cantidad || 0,
            sortable: true,
            right: true,
            grow: 1
        },
        {
            name: 'Unidad',
            selector: row => row?.unidad?.nombre || 'Sin definir',
            sortable: true,
            wrap: true,
            grow: 1
        },
        {
            name: 'Precio unitario',
            selector: row => row?.precio_unitario || 0,
            sortable: true,
            right: true,
            cell: row => (
                <div style={{ width: '100%', textAlign: 'right' }}>
                    {formatMoney(row?.precio_unitario)}
                </div>
            ),
            grow: 1
        },
        {
            name: 'IVA',
            width: '70px',
            center: true,
            selector: row => (row?.iva ? 1 : 0),
            sortable: true,
            cell: row => (row?.iva ? 'Sí' : 'No')
        },
        {
            name: 'Importe',
            selector: row => Number(row?.cantidad || 0) * Number(row?.precio_unitario || 0) * (row?.iva ? 1.16 : 1),
            sortable: true,
            right: true,
            cell: row => (
                <div style={{ width: '100%', textAlign: 'right' }}>
                    {formatMoney(Number(row?.cantidad || 0) * Number(row?.precio_unitario || 0) * (row?.iva ? 1.16 : 1))}
                </div>
            ),
            grow: 1
        },
        {
            name: 'Opciones',
            width: '120px',
            cell: row => {
                const ordenEntregada = Number(row?.orden_salida?.status) === 2;

                if (ordenEntregada) {
                    return <span className="text-muted">—</span>;
                }

                return (
                    <div className="d-flex align-items-center gap-2">
                        <button
                            type="button"
                            className="btn btn-sm btn-link px-2"
                            title="Editar"
                            onClick={() => setConceptoEditar(row)}
                        >
                            <i className="fa fa-edit text-warning" />
                        </button>

                        <button
                            type="button"
                            className="btn btn-sm btn-link px-2"
                            title="Eliminar"
                            onClick={() => setConceptoEliminar(row)}
                        >
                            <i className="fas fa-trash text-danger" />
                        </button>
                    </div>
                );
            }
        }
    ]), []);

    const paginationOptions = {
        rowsPerPageText: 'Filas por página',
        rangeSeparatorText: 'de',
        selectAllRowsItem: true,
        selectAllRowsItemText: 'Todos'
    };

    return (
        <Fragment>
            <div className="d-flex justify-content-end mb-3">
                <div className="d-flex align-items-center gap-2 w-100 justify-content-end">
                    <div style={{ minWidth: 280 }}>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Buscar en la tabla..."
                            value={textoFiltro}
                            onChange={onChangeFiltroTabla}
                        />
                    </div>

                    <button type="button" className="btn btn-primary" onClick={() => setModalNuevo(true)}>
                        <i className="feather icon-plus" /> Agregar concepto
                    </button>
                </div>
            </div>

            <div className="mt-2">
                <DataTable
                    columns={columns}
                    data={filtrado || []}
                    pagination
                    paginationComponentOptions={paginationOptions}
                    highlightOnHover
                    striped
                    responsive
                    persistTableHead
                    noDataComponent="Sin conceptos para mostrar"
                />
            </div>

            <NuevoConceptoModal
                show={modalNuevo}
                idProyecto={idProyecto}
                onHide={() => setModalNuevo(false)}
                onSuccess={() => setModalNuevo(false)}
            />

            <EditarConceptoModal
                show={!!conceptoEditar}
                idProyecto={idProyecto}
                concepto={conceptoEditar}
                onHide={() => setConceptoEditar(null)}
                onSuccess={() => setConceptoEditar(null)}
            />

            <EliminarConceptoModal
                show={!!conceptoEliminar}
                idProyecto={idProyecto}
                idConcepto={conceptoEliminar?._id || null}
                onHide={() => setConceptoEliminar(null)}
            />
        </Fragment>
    );
};

export default ConceptosProyecto;
